/**
 * QuickBillForm — Quick Bill as a tax invoice you fill in, the way a merchant
 * writes a bill by hand.
 *
 *  ┌ Shop · address · GSTIN ─────────────────────── TAX INVOICE · QUICK BILL ┐
 *  │ Buyer (bill to) ▾      │ Inv no. │ Inv date │ Deliv. note │ Note date   │
 *  │ Address                │ Order no│ Ord date │ Disp. doc   │ Vehicle no. │
 *  │ GSTIN │ Phone │ Ship to│ Disp. through │ Destination │ Terms of delivery │
 *  ├ # │ Description ▾ │ HSN ▾ │ Qty │ Unit │ Rate │ GST % │ Amount ──────────┤
 *  │   … the item lines take all remaining height and scroll on their own … │
 *  ├ Amount in words · Notes · Terms         │ Taxable · GST · Round · Total ┤
 *  └────────────────────────────────────────────────────────────────────────┘
 *
 * The header is a band of small ruled boxes, as on a printed tax invoice, so
 * the item area gets the page (owner's feedback, 2026-09-29).
 *
 * Everything is typed freely; what the shop already knows is offered while
 * typing (billing/quickBillSuggest.ts). What is typed for the first time is
 * kept: a new buyer is saved as a customer when payment starts, and new items
 * join the catalogue once the bill is saved (billing/quickBillCatalogue.ts).
 *
 * The maths is the billing engine's (billing/quickBillForm.ts). "Continue to
 * payment" loads the rows into the ordinary cart and hands over to the normal
 * CheckoutScreen, so payment, printing and the sale writer are the same code
 * as every other bill. Dispatch details are saved as labelled lines in the
 * invoice's Terms (billing/invoiceExtras.ts) — owner's decision, no schema
 * change.
 */
import { useErrorPopup } from '@/hooks/useErrorPopup';
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';
import { Q } from '@nozbe/watermelondb';
import { FilePenLine, Package, Trash2, X } from 'lucide-react';
import { GSTIN_REGEX, VALID_GST_RATES } from '@bigxbook/gst-core';
import { database } from '@/db';
import Store from '@/db/models/Store';
import { GstFiling } from '@/db/models/System';
import { useAppStore } from '@/store/useAppStore';
import { getCartConfig, useCartStore } from '@/store/useCartStore';
import { useQuickBillStore } from '@/store/useQuickBillStore';
import { Button } from '@/components/ui/Button';
import { SuggestInput } from '@/components/ui/SuggestInput';
import { cn } from '@/utils/cn';
import { formatCurrency } from '@/utils/format';
import { loadStoreGstIdentity } from '@/gst/storeIdentity';
import { resolveInterState } from '@/utils/placeOfSupply';
import { amountInWordsINR } from '@/printing/templates/invoiceFormat';
import {
  billDateNotice, billPeriod, billTimestamp, filedPeriodError, parseDateInput, toDateInput,
} from '@/billing/billDate';
import { DISPATCH_FIELDS, composeTerms, type DispatchKey } from '@/billing/invoiceExtras';
import { computeForm, isBlankRow, rowProduct, validateForm, type QuickBillRow } from '@/billing/quickBillForm';
import { buyerNameProblem, buyerProblems, resolveBuyer } from '@/billing/billToParty';
import {
  suggestBuyers, suggestHsn, suggestItems,
  type BuyerSuggestion, type HsnSuggestion, type ItemSuggestion,
} from '@/billing/quickBillSuggest';
import { describeError } from '@/utils/errorMessage';
import { useIsWide } from '@/hooks/useMediaQuery';

/** Inputs inside ruled cells: borderless, like writing on the printed form. */
const cellInput =
  'h-8 w-full bg-transparent px-2 text-body text-fg-primary outline-none placeholder:text-fg-tertiary';
const boxInput =
  'h-6 w-full bg-transparent text-body text-fg-primary outline-none placeholder:text-fg-tertiary';
const rule = 'border-stroke-hairline/15';

/**
 * The reference boxes, four to a row as on a printed tax invoice. Invoice no.
 * and date come first and are laid out by hand; these follow.
 */
const REF_BOXES: { key: DispatchKey; span?: 2 }[] = [
  { key: 'deliveryNote' }, { key: 'deliveryNoteDate' },
  { key: 'buyerOrderNo' }, { key: 'buyerOrderDate' }, { key: 'dispatchDocNo' }, { key: 'vehicleNo' },
  { key: 'dispatchedThrough' }, { key: 'destination' }, { key: 'termsOfDelivery', span: 2 },
];
const DATE_DETAILS = new Set<DispatchKey>(['deliveryNoteDate', 'buyerOrderDate']);
const labelFor = (k: DispatchKey) => DISPATCH_FIELDS.find((f) => f.key === k)!.label;

/** One ruled box: a small caption over a borderless input. */
function Box({ caption, children, className }: { caption: ReactNode; children: ReactNode; className?: string }) {
  return (
    <div className={cn('min-w-0 border-b border-r px-2 pb-0.5 pt-1', rule, className)}>
      <div className="truncate text-[10px] uppercase tracking-wide text-fg-tertiary">{caption}</div>
      {children}
    </div>
  );
}

interface SellerView { name: string; address: string; gstin: string; phone: string; stateCode: string | null }

export function QuickBillForm({ onContinue }: { onContinue: () => void }) {
  const activeStoreId = useAppStore((s) => s.activeStoreId);

  const billDate = useCartStore((s) => s.billDate);
  const setBillDate = useCartStore((s) => s.setBillDate);
  const setQuickBill = useCartStore((s) => s.setQuickBill);

  const d = useQuickBillStore();
  const [problems, setProblems] = useState<string[]>([]);
  useErrorPopup(problems.join('  ·  '), () => setProblems([]));
  const [busy, setBusy] = useState(false);

  // ── Seller header and home state ────────────────────────────────────────
  const [seller, setSeller] = useState<SellerView | null>(null);
  useEffect(() => {
    if (!activeStoreId) return;
    let alive = true;
    void Promise.all([
      database.get<Store>('stores').find(activeStoreId),
      loadStoreGstIdentity(activeStoreId),
    ]).then(([store, id]) => {
      if (!alive) return;
      setSeller({
        name: id.storeName || store.name,
        address: [store.address, store.city, store.state, store.pincode].filter(Boolean).join(', '),
        gstin: id.gstin ?? '',
        phone: store.phone ?? '',
        stateCode: id.homeStateCode,
      });
    }).catch(() => { /* header stays minimal; the bill still works */ });
    return () => { alive = false; };
  }, [activeStoreId]);

  // ── Place of supply: from the buyer's GSTIN unless overridden ───────────
  const derivedInterState = resolveInterState({ gstin: d.buyer.gstin.trim().toUpperCase() }, seller?.stateCode);
  const isInterState = d.interStateOverride ?? derivedInterState ?? false;

  // ── The engine's numbers for what is on the form ────────────────────────
  const config = getCartConfig();
  const mode = useMemo(() => ({ ratesIncludeGst: d.ratesIncludeGst, config }), [d.ratesIncludeGst, config]);
  const form = useMemo(() => computeForm(d.rows, isInterState, mode), [d.rows, isInterState, mode]);
  const amountByKey = new Map(form.lines.map((l) => [l.key, l.taxable]));

  // ── Invoice date ─────────────────────────────────────────────────────────
  const dateValue = billDate ?? toDateInput(new Date());
  const dateNotice = billDateNotice(dateValue);
  const [filedError, setFiledError] = useState<string | null>(null);
  useErrorPopup(filedError);
  useEffect(() => {
    if (!activeStoreId) return;
    const period = billPeriod(billTimestamp(dateValue));
    let alive = true;
    void database.get<GstFiling>('gst_filings')
      .query(Q.where('store_id', activeStoreId), Q.where('period', period), Q.where('is_deleted', false))
      .fetch()
      .then((rows) => { if (alive) setFiledError(filedPeriodError(period, rows)); })
      .catch(() => { if (alive) setFiledError(null); });
    return () => { alive = false; };
  }, [activeStoreId, dateValue]);

  // ── Suggestion fetchers (stable, see SuggestInput) ───────────────────────
  const fetchBuyers = useCallback((q: string) => suggestBuyers(activeStoreId ?? '', q), [activeStoreId]);
  const fetchItems = useCallback(
    (q: string) => suggestItems(activeStoreId ?? '', q, d.ratesIncludeGst, config.isTaxInclusive),
    [activeStoreId, d.ratesIncludeGst, config.isTaxInclusive],
  );
  const fetchHsn = useCallback((q: string) => suggestHsn(q), []);

  const pickBuyer = (b: BuyerSuggestion) => d.pickBuyer({
    customerId: b.kind === 'customer' ? b.id : null,
    supplierId: b.kind === 'supplier' ? b.id : null,
    name: b.name, address: b.address, gstin: b.gstin, phone: b.phone,
  });

  const pickItem = (row: QuickBillRow, s: ItemSuggestion) => d.updateRow(row.key, {
    catalogue: s.product, name: s.name, hsn: s.hsn, gstRate: s.gstRate, cessRate: s.cessRate, unit: s.unit, rate: s.rate,
  });

  const pickHsn = (row: QuickBillRow, h: HsnSuggestion) => d.updateRow(row.key, { hsn: h.code, gstRate: h.gstRate, cessRate: h.cessRate });

  // ── Continue to payment ──────────────────────────────────────────────────
  const handleContinue = useCallback(async () => {
    if (busy || !activeStoreId) return;
    const found = [
      ...validateForm(d.rows, d.buyer.name),
      ...buyerProblems(d.buyer, GSTIN_REGEX),
      ...(filedError ? [filedError] : []),
    ];
    setProblems(found);
    if (found.length) return;

    setBusy(true);
    try {
      const clash = await buyerNameProblem(activeStoreId, d.buyer);
      if (clash) { setProblems([clash]); return; }

      // A buyer typed for the first time is saved now, so the invoice can
      // point at them; details typed for a known buyer fill their blanks.
      const customer = await resolveBuyer(activeStoreId, d.buyer);

      const cart = useCartStore.getState();
      const keepDate = cart.billDate;
      cart.clearCart();
      cart.setBillDate(keepDate);
      cart.setIsInterState(isInterState);
      for (const r of d.rows) {
        if (isBlankRow(r)) continue;
        cart.addItem(rowProduct(r, mode), r.qty);
      }
      cart.setCustomer({ id: customer.id, name: customer.name, phone: customer.phone, gstin: customer.gstin, state: customer.state });
      cart.setNotes(d.remarks.trim());
      cart.setTerms(composeTerms(d.details, d.freeTerms));
      onContinue();
    } catch (err) {
      setProblems([describeError(err, 'Could not prepare the bill. Please try again.')]);
    } finally {
      setBusy(false);
    }
  }, [busy, activeStoreId, d, filedError, isInterState, mode, onContinue]);

  // F9 continues, as it does on the normal cart.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'F9') { e.preventDefault(); void handleContinue(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleContinue]);

  const t = form.totals;
  // Phones get one card per line instead of the nine-column table, and the
  // whole invoice scrolls as one page instead of pinning the header.
  const wide = useIsWide();
  const words = t.totalAmount > 0 ? amountInWordsINR(t.totalAmount) : '—';

  // Enter on an item line goes to the next line's description, the way an
  // invoice is written: one item, then the next. Tab still walks across the
  // columns. A suggestion list that is open and highlighted takes Enter first
  // (SuggestInput prevents default when it picks).
  const formRef = useRef<HTMLDivElement>(null);
  const onLineEnter = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Enter' || e.defaultPrevented || e.shiftKey || e.altKey || e.ctrlKey || e.metaKey) return;
    const el = e.target as HTMLElement;
    if (el.tagName !== 'INPUT' && el.tagName !== 'SELECT') return;
    const line = el.closest<HTMLElement>('[data-line]');
    if (!line) return;
    e.preventDefault();
    const next = Number(line.dataset.line) + 2;
    const find = () => formRef.current?.querySelector<HTMLInputElement>(`[aria-label="Line ${next} description"]`);
    // Move NOW: waiting even a frame lets a fast typist's next letters land in
    // the cell being left (a number cell then drops them). The form always
    // keeps a blank line after the last one, so the next line is nearly always
    // there already; only if it is not, wait for it to render.
    const target = find();
    if (target) target.focus();
    else requestAnimationFrame(() => find()?.focus());
  };

  const mobileLines = (
    <div className="space-y-2 p-2">
      {d.rows.map((r, i) => {
        const blank = isBlankRow(r);
        const amount = amountByKey.get(r.key);
        const needsHsn = !blank && r.rate > 0 && !r.hsn.trim();
        return (
          <div key={r.key} className={cn('rounded-md border', rule, blank && 'border-dashed')} data-testid="qb-line" data-line={i}>
            <div className="flex items-center justify-between px-2 pt-1.5 text-[11px] text-fg-tertiary">
              <span className="inline-flex items-center gap-1">
                {blank ? 'New line' : `Line ${i + 1}`}
                {r.catalogue && <><Package size={11} className="text-accent-ink" /> from catalogue</>}
              </span>
              {!blank && (
                <button type="button" tabIndex={-1} onClick={() => d.removeRow(r.key)} aria-label={`Remove line ${i + 1}`}
                  className="p-1 text-fg-tertiary hover:text-danger-ink">
                  <Trash2 size={14} />
                </button>
              )}
            </div>
            <div className="px-0.5">
              <SuggestInput<ItemSuggestion>
                value={r.name}
                onChange={(v) => d.updateRow(r.key, { name: v, catalogue: null })}
                fetch={fetchItems}
                view={(sg) => ({
                  primary: sg.name,
                  secondary: [sg.hsn && `HSN ${sg.hsn}`, formatCurrency(sg.rate), `${sg.gstRate}%`, sg.stock !== null ? `${sg.stock} in stock` : null].filter(Boolean).join(' · '),
                  tag: sg.kind === 'catalogue' ? 'Catalogue' : 'Typed before',
                })}
                onPick={(sg) => pickItem(r, sg)}
                hint={(q, rs) => (q && !rs.some((sg) => sg.name.toLowerCase() === q.toLowerCase())
                  ? `New item “${q}” — added to your catalogue when saved`
                  : null)}
                placeholder="Type an item…"
                ariaLabel={`Line ${i + 1} description`}
                className={cn(cellInput, 'font-medium')}
                listClassName="min-w-[calc(100vw-2rem)]"
              />
            </div>
            <div className={cn('grid grid-cols-3 border-t text-body', rule)}>
              <Box caption="HSN/SAC" className={cn(needsHsn && 'bg-warning/10')}>
                <SuggestInput<HsnSuggestion>
                  value={r.hsn}
                  onChange={(v) => d.updateRow(r.key, { hsn: v.replace(/\s/g, '') })}
                  fetch={fetchHsn}
                  view={(h) => ({ primary: `${h.code} · ${h.gstRate}%`, secondary: h.description })}
                  onPick={(h) => pickHsn(r, h)}
                  isExact={(h, q) => h.code === q}
                  ariaLabel={`Line ${i + 1} HSN`}
                  className={cn(boxInput, 'tabular-nums', needsHsn && 'placeholder:text-warning-ink')}
                  placeholder={needsHsn ? 'Add HSN' : ''}
                  listClassName="min-w-[calc(100vw-2rem)]"
                  inputMode="numeric"
                />
              </Box>
              <Box caption="Qty">
                <NumCell value={r.qty} onValue={(qty) => d.updateRow(r.key, { qty })} ariaLabel={`Line ${i + 1} quantity`} compact />
              </Box>
              <Box caption="Unit" className="!border-r-0">
                <input className={cn(boxInput, 'uppercase')} aria-label={`Line ${i + 1} unit`} value={r.unit}
                  onChange={(e) => d.updateRow(r.key, { unit: e.target.value.toUpperCase() })} />
              </Box>
              <Box caption={d.ratesIncludeGst ? 'Rate (incl. GST)' : 'Rate'} className="!border-b-0">
                <NumCell value={r.rate} onValue={(rate) => d.updateRow(r.key, { rate })} ariaLabel={`Line ${i + 1} rate`} compact />
              </Box>
              <Box caption="GST %" className="!border-b-0">
                <select aria-label={`Line ${i + 1} GST rate`} className={boxInput} value={r.gstRate}
                  onChange={(e) => d.updateRow(r.key, { gstRate: Number(e.target.value) })}>
                  {VALID_GST_RATES.map((g) => <option key={g} value={g}>{g}%</option>)}
                </select>
              </Box>
              <Box caption="Amount" className="!border-b-0 !border-r-0">
                <div className="flex h-6 items-center justify-end tabular-nums text-fg-primary">
                  {amount !== undefined ? formatCurrency(amount) : '—'}
                </div>
              </Box>
            </div>
          </div>
        );
      })}
      <label className="flex items-center gap-1.5 px-1 text-[12px] text-fg-secondary">
        <input type="checkbox" className="h-3.5 w-3.5" checked={d.ratesIncludeGst}
          onChange={(e) => d.setRatesIncludeGst(e.target.checked)} aria-label="Rates include GST" />
        Rates include GST
      </label>
    </div>
  );

  return (
    <div ref={formRef} className="qb-form flex h-full flex-col gap-2" data-testid="quick-bill-form" onKeyDown={onLineEnter}>
      {/* The invoice fills the height; only the item lines scroll. */}
      <div className={cn('mx-auto flex min-h-0 w-full max-w-[1200px] flex-1 flex-col rounded-lg border bg-bg-surface', wide ? 'overflow-hidden' : 'overflow-y-auto', rule)}>
        {/* Seller: one slim strip */}
        <div className={cn('flex shrink-0 items-center gap-3 border-b bg-bg-subtle/60 px-3 py-1.5', rule)}>
          <FilePenLine size={15} className="shrink-0 text-accent-ink" />
          <span className="shrink-0 font-semibold text-fg-primary">{seller?.name ?? 'Your store'}</span>
          <span className="min-w-0 truncate text-[12px] text-fg-secondary">
            {[seller?.address, seller?.gstin && `GSTIN ${seller.gstin}`, seller?.phone && `Ph ${seller.phone}`].filter(Boolean).join(' · ')}
          </span>
          <span className="ml-auto shrink-0 text-[11px] font-semibold uppercase tracking-widest text-fg-secondary">Tax invoice · Quick Bill</span>
        </div>

        {/* Buyer | reference boxes: ruled and compact */}
        <div className={cn('grid shrink-0 grid-cols-1 border-b md:grid-cols-[5fr_7fr]', rule)}>
          <div className={cn('grid grid-cols-2 border-b md:grid-cols-[1.35fr_1fr_1.35fr] md:border-b-0 md:border-r', rule)}>
            <Box className="col-span-3 !border-r-0" caption={
              <>Buyer (bill to){' '}
                <span className="normal-case tracking-normal">
                  · {d.buyer.customerId ? 'saved customer' : d.buyer.supplierId ? 'supplier' : d.buyer.name.trim() ? 'new, saved on continue' : 'type a name, phone or GSTIN'}
                </span>
              </>
            }>
              <SuggestInput<BuyerSuggestion>
                value={d.buyer.name}
                onChange={(v) => d.setBuyerField('name', v)}
                fetch={fetchBuyers}
                view={(b) => ({ primary: b.name, secondary: [b.gstin, b.phone, b.address].filter(Boolean).join(' · '), tag: b.kind === 'supplier' ? 'Supplier' : 'Customer' })}
                onPick={pickBuyer}
                hint={(q, rs) => (q && !rs.some((b) => b.name.toLowerCase() === q.toLowerCase())
                  ? `New buyer “${q}” — saved when you continue`
                  : null)}
                ariaLabel="Buyer name"
                className={cn(boxInput, 'font-medium')}
                testId="qb-buyer"
              />
            </Box>
            <Box className="col-span-3 !border-r-0" caption="Address">
              <input className={boxInput} aria-label="Buyer address" value={d.buyer.address}
                onChange={(e) => d.setBuyerField('address', e.target.value)} />
            </Box>
            <Box caption="GSTIN" className="md:!border-b-0">
              <input className={cn(boxInput, 'uppercase tabular-nums')} aria-label="Buyer GSTIN" maxLength={15}
                value={d.buyer.gstin} onChange={(e) => d.setBuyerField('gstin', e.target.value.toUpperCase())} />
            </Box>
            <Box caption="Phone" className="!border-r-0 md:!border-b-0 md:!border-r">
              <input className={cn(boxInput, 'tabular-nums')} aria-label="Buyer phone" inputMode="numeric"
                value={d.buyer.phone} onChange={(e) => d.setBuyerField('phone', e.target.value)} />
            </Box>
            <Box caption="Ship to (if different)" className="col-span-2 !border-b-0 !border-r-0 md:col-span-1">
              <input className={boxInput} aria-label="Ship to" value={d.details.shipTo ?? ''}
                onChange={(e) => d.setDetail('shipTo', e.target.value)} />
            </Box>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4">
            <Box caption="Invoice no.">
              <div className="flex h-6 items-center text-[12px] text-fg-tertiary">Assigned on save</div>
            </Box>
            <Box caption="Invoice date">
              <input type="date" aria-label="Invoice date" className={boxInput} value={dateValue}
                onChange={(e) => { if (parseDateInput(e.target.value)) setBillDate(e.target.value); }} />
            </Box>
            {REF_BOXES.map(({ key, span }, idx) => {
              // Boxes 0-1 share row 1 with invoice no./date; the rest fill rows 2-3.
              const lastInRow = span === 2 || idx === 1 || idx === 5;
              const lastRow = idx >= 6;
              return (
                <Box key={key} caption={labelFor(key)}
                  className={cn(span === 2 && 'col-span-2', lastInRow && '!border-r-0', lastRow && '!border-b-0')}>
                  <input
                    type={DATE_DETAILS.has(key) ? 'date' : 'text'}
                    aria-label={labelFor(key)}
                    className={boxInput}
                    value={d.details[key] ?? ''}
                    onChange={(e) => d.setDetail(key, e.target.value)}
                  />
                </Box>
              );
            })}
          </div>
        </div>

        {/* One slim line, only when the date needs saying */}
        {!filedError && dateNotice && (
          <div className={cn('flex shrink-0 items-center gap-1.5 border-b bg-warning/5 px-3 py-1 text-[12px] text-warning-ink', rule)}>
            {dateNotice}
          </div>
        )}

        {/* Items: take all remaining height and scroll on their own */}
        {!wide ? mobileLines : (
        <div className="min-h-0 flex-1 overflow-auto">
          <table className="h-full w-full table-fixed border-collapse text-body">
            <colgroup>
              <col className="w-10" /><col /><col className="w-32" /><col className="w-20" />
              <col className="w-20" /><col className="w-32" /><col className="w-20" /><col className="w-32" /><col className="w-9" />
            </colgroup>
            <thead className="sticky top-0 z-10 bg-bg-surface">
              <tr className="text-left text-[11px] uppercase text-fg-secondary shadow-[inset_0_-1px_0_rgba(14,21,25,0.12)]">
                <th className="px-2 py-1.5 font-medium">#</th>
                <th className="px-2 py-1.5 font-medium">Description of goods</th>
                <th className="px-2 py-1.5 font-medium">HSN/SAC</th>
                <th className="px-2 py-1.5 text-right font-medium">Qty</th>
                <th className="px-2 py-1.5 font-medium">Unit</th>
                <th className="px-2 py-1.5 text-right font-medium">
                  Rate
                  <label className="ml-1.5 inline-flex cursor-pointer items-center gap-1 normal-case text-fg-tertiary" title="Tick if the rates you type include GST">
                    <input type="checkbox" className="h-3 w-3" checked={d.ratesIncludeGst}
                      onChange={(e) => d.setRatesIncludeGst(e.target.checked)} aria-label="Rates include GST" />
                    incl. GST
                  </label>
                </th>
                <th className="px-2 py-1.5 text-right font-medium">GST %</th>
                <th className="px-2 py-1.5 text-right font-medium">Amount</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {d.rows.map((r, i) => {
                const blank = isBlankRow(r);
                const amount = amountByKey.get(r.key);
                // Row notes are shown as a mark and a tint, not an extra line:
                // every line stays one row high, so more of them fit.
                const fromCatalogue = r.catalogue
                  ? `From catalogue${r.catalogue.trackInventory ? ' · stock will reduce' : ''}`
                  : null;
                const needsHsn = !blank && r.rate > 0 && !r.hsn.trim();
                return (
                  <tr key={r.key} className={cn('h-9 border-b border-stroke-hairline/10 align-top', blank && '[&_input]:text-fg-tertiary [&_select]:text-fg-tertiary')} data-testid="qb-line" data-line={i}>
                    <td className="px-2 py-2 text-[12px] text-fg-tertiary tabular-nums" title={fromCatalogue ?? undefined}>
                      {blank ? '' : (
                        <span className="inline-flex items-center gap-0.5">
                          {i + 1}
                          {fromCatalogue && <Package size={11} className="text-accent-ink" aria-label={fromCatalogue} />}
                        </span>
                      )}
                    </td>
                    <td className="border-l border-stroke-hairline/10">
                      <SuggestInput<ItemSuggestion>
                        value={r.name}
                        onChange={(v) => d.updateRow(r.key, { name: v, catalogue: null })}
                        fetch={fetchItems}
                        view={(s) => ({
                          primary: s.name,
                          secondary: [s.hsn && `HSN ${s.hsn}`, `${formatCurrency(s.rate)}${d.ratesIncludeGst ? ' incl.' : ''}`, `${s.gstRate}%`, s.stock !== null ? `${s.stock} in stock` : null].filter(Boolean).join(' · '),
                          tag: s.kind === 'catalogue' ? 'Catalogue' : 'Typed before',
                        })}
                        onPick={(s) => pickItem(r, s)}
                        hint={(q, rs) => (q && !rs.some((s) => s.name.toLowerCase() === q.toLowerCase())
                          ? `New item “${q}” — added to your catalogue when the bill is saved`
                          : null)}
                        placeholder={blank ? 'Type an item…' : ''}
                        ariaLabel={`Line ${i + 1} description`}
                        className={cellInput}
                        listClassName="min-w-[26rem]"
                      />
                    </td>
                    <td className={cn('border-l border-stroke-hairline/10', needsHsn && 'bg-warning/10')}
                      title={needsHsn ? 'HSN/SAC is required on B2B tax invoices' : undefined}>
                      <SuggestInput<HsnSuggestion>
                        value={r.hsn}
                        onChange={(v) => d.updateRow(r.key, { hsn: v.replace(/\s/g, '') })}
                        fetch={fetchHsn}
                        view={(h) => ({ primary: `${h.code} · ${h.gstRate}%`, secondary: h.description })}
                        onPick={(h) => pickHsn(r, h)}
                        isExact={(h, q) => h.code === q}
                        ariaLabel={`Line ${i + 1} HSN`}
                        className={cn(cellInput, 'tabular-nums', needsHsn && 'placeholder:text-warning-ink')}
                        placeholder={needsHsn ? 'Add HSN' : ''}
                        listClassName="min-w-[22rem]"
                        inputMode="numeric"
                      />
                    </td>
                    <td className="border-l border-stroke-hairline/10">
                      <NumCell value={r.qty} onValue={(qty) => d.updateRow(r.key, { qty })} ariaLabel={`Line ${i + 1} quantity`} />
                    </td>
                    <td className="border-l border-stroke-hairline/10">
                      <input className={cn(cellInput, 'uppercase')} aria-label={`Line ${i + 1} unit`} value={r.unit}
                        onChange={(e) => d.updateRow(r.key, { unit: e.target.value.toUpperCase() })} />
                    </td>
                    <td className="border-l border-stroke-hairline/10">
                      <NumCell value={r.rate} onValue={(rate) => d.updateRow(r.key, { rate })} ariaLabel={`Line ${i + 1} rate`} />
                    </td>
                    <td className="border-l border-stroke-hairline/10">
                      <select aria-label={`Line ${i + 1} GST rate`} className={cn(cellInput, 'text-right')} value={r.gstRate}
                        onChange={(e) => d.updateRow(r.key, { gstRate: Number(e.target.value) })}>
                        {VALID_GST_RATES.map((g) => <option key={g} value={g}>{g}%</option>)}
                      </select>
                    </td>
                    <td className="border-l border-stroke-hairline/10 px-2 py-2 text-right tabular-nums text-fg-primary">
                      {amount !== undefined ? formatCurrency(amount) : ''}
                    </td>
                    <td className="text-center">
                      {!blank && (
                        <button type="button" tabIndex={-1} onClick={() => d.removeRow(r.key)} aria-label={`Remove line ${i + 1}`}
                          className="mt-2 text-fg-tertiary hover:text-danger-ink">
                          <Trash2 size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {/* Filler: carries the column rules to the bottom, as on a printed invoice. */}
              <tr aria-hidden className="h-full">
                <td /><td className="border-l border-stroke-hairline/10" /><td className="border-l border-stroke-hairline/10" />
                <td className="border-l border-stroke-hairline/10" /><td className="border-l border-stroke-hairline/10" />
                <td className="border-l border-stroke-hairline/10" /><td className="border-l border-stroke-hairline/10" />
                <td className="border-l border-stroke-hairline/10" /><td />
              </tr>
            </tbody>
          </table>
        </div>
        )}

        {/* Footer: words, notes, terms | totals, in one compact band */}
        <div className={cn('grid shrink-0 grid-cols-1 border-t md:grid-cols-[1fr_19rem]', rule)}>
          <div className={cn('grid grid-cols-1 border-b sm:grid-cols-2 md:border-b-0 md:border-r', rule)}>
            <Box caption="Amount in words" className="col-span-2 !border-r-0">
              <div className="truncate py-0.5 text-body text-fg-primary" data-testid="qb-words" title={words}>{words}</div>
            </Box>
            <Box caption="Notes" className="!border-b-0">
              <input className={boxInput} aria-label="Notes" value={d.remarks} onChange={(e) => d.setRemarks(e.target.value)} />
            </Box>
            <Box caption="Terms" className="!border-b-0 !border-r-0">
              <input className={boxInput} aria-label="Terms" placeholder="Goods once sold will not be taken back."
                value={d.freeTerms} onChange={(e) => d.setFreeTerms(e.target.value)} />
            </Box>
          </div>
          <div className="space-y-0.5 px-3 py-1.5 text-[13px] tabular-nums" data-testid="qb-totals">
            <div className="flex items-center justify-between text-fg-secondary">
              <span>Taxable value</span><span>{formatCurrency(t.taxableAmount)}</span>
            </div>
            <label className="flex cursor-pointer items-center justify-between text-fg-secondary" title="Set from the buyer's GSTIN. Tick to override.">
              <span className="inline-flex items-center gap-1.5">
                <input type="checkbox" className="h-3.5 w-3.5" checked={isInterState}
                  onChange={(e) => d.setInterStateOverride(e.target.checked)} aria-label="Inter-state (IGST)" />
                {isInterState ? 'IGST' : 'CGST + SGST'}
                <span className="text-[11px] text-fg-tertiary">
                  {d.interStateOverride === null && derivedInterState !== null ? '· from GSTIN' : isInterState ? '· other state' : ''}
                </span>
              </span>
              <span>{formatCurrency(isInterState ? t.igst : t.cgst + t.sgst)}</span>
            </label>
            {t.cess > 0 && <TotalRow label="Cess" value={t.cess} />}
            {t.roundOff !== 0 && <TotalRow label="Round off" value={t.roundOff} signed />}
            <div className={cn('flex items-baseline justify-between border-t pt-1', rule)}>
              <span className="font-medium text-fg-primary">Total</span>
              <span className="text-[18px] font-semibold text-fg-primary" data-testid="qb-total">{formatCurrency(t.totalAmount)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Action bar */}
      <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1.5">
        <Button size="sm" variant="ghost" onClick={() => {
          // The cart only ever holds what this form loaded into it (Quick Bill
          // cannot be entered with a bill in progress), so leaving empties it —
          // otherwise the form's lines and buyer reappear in normal billing.
          d.reset();
          useCartStore.getState().clearCart();
          setQuickBill(false);
        }}>
          <X size={14} className="mr-1" /> Exit Quick Bill
        </Button>
        <Button size="sm" variant="ghost" onClick={() => { d.reset(); useCartStore.getState().clearCart(); setProblems([]); }}>Clear form</Button>
        <div className="hidden md:block md:flex-1" />
        <Button size="lg" className="w-full md:w-auto" onClick={() => { void handleContinue(); }} disabled={busy}>
          {busy ? 'Preparing…' : `Continue to payment · ${formatCurrency(t.totalAmount)}${wide ? ' · F9' : ''}`}
        </Button>
      </div>
    </div>
  );
}


function TotalRow({ label, value, signed }: { label: string; value: number; signed?: boolean }) {
  return (
    <div className="flex justify-between text-fg-secondary">
      <span>{label}</span>
      <span>{signed && value > 0 ? '+' : ''}{signed ? value.toFixed(2) : formatCurrency(value)}</span>
    </div>
  );
}

/**
 * A number cell that lets you type "12." and "0.5" — a plain controlled
 * number input would parse "12." to 12 and eat the point.
 */
function NumCell({ value, onValue, ariaLabel, compact }: { value: number; onValue: (n: number) => void; ariaLabel: string; compact?: boolean }) {
  const [text, setText] = useState(value ? String(value) : '');
  const [focused, setFocused] = useState(false);
  useEffect(() => { if (!focused) setText(value ? String(value) : ''); }, [value, focused]);
  return (
    <input
      className={cn(compact ? boxInput : cellInput, 'text-right tabular-nums')}
      inputMode="decimal"
      aria-label={ariaLabel}
      value={text}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onChange={(e) => {
        const v = e.target.value.replace(/[^\d.]/g, '');
        setText(v);
        const n = parseFloat(v);
        onValue(Number.isFinite(n) ? n : 0);
      }}
    />
  );
}
